// Typed wrappers around the My.Check endpoints, scoped to `/check/api/v1/*`.
//
// The runtime is the shared @my/platform/http transport, so bearer token,
// X-App-Key, X-Idempotency-Key and Result<T> error mapping all behave
// identically to every other call in the suite.
//
// Types are hand-written against the BE DTOs (same convention as the Resources
// and Roster sections of @my/directory-api) until the Check API is stable
// enough to codegen from. Enum unions mirror the C# enums by NAME, because the
// BE serialises them as strings — the numeric values never cross the wire.
import { request, type ApiInfo } from '@my/platform/http';

// ────────────────────────────────────────────────────────────────────────────
// Enums — mirror My.Check.Domain.Enums
// ────────────────────────────────────────────────────────────────────────────

/** `CheckRegime`. Wac is the statutory walk-around; PreUse is company policy. */
export type CheckRegime = 'Wac' | 'PreUse';

/**
 * `UnitType`. NOTE: this taxonomy has no counterpart in My.Directory, whose
 * ResourceType is Driver | Truck | Rigid | Trailer | Other — so Lgv, Bus,
 * Minibus, Car, Motorcycle and Cycle cannot yet be represented as a resource.
 * Unresolved; bites when templates get assigned to vehicles.
 */
export type UnitType =
  | 'Hgv'
  | 'Rigid'
  | 'Lgv'
  | 'Trailer'
  | 'Bus'
  | 'Minibus'
  | 'Car'
  | 'Motorcycle'
  | 'Cycle';

/** `WacType`. Type1 is Yes/No only; Type2 adds severity, a note and a photo. */
export type WacType = 'Type1' | 'Type2';

export type QuestionSeverity = 'Low' | 'Medium' | 'High';

/** `TemplateVersionStatus`. A Published version is immutable. */
export type TemplateVersionStatus = 'Draft' | 'Published' | 'Archived';

// ────────────────────────────────────────────────────────────────────────────
// Question catalogue
// ────────────────────────────────────────────────────────────────────────────

export interface CheckQuestionCatalogueSection {
  key: string;
  label: string;
  order: number;
}

export interface CheckQuestionCatalogueEntry {
  key: string;
  section: string;
  label: string;
  /** Lower-case `UnitType` names — `"hgv"`, `"rigid"`, … */
  appliesTo: string[];
  mandatory: boolean;
  defaultSeverity: QuestionSeverity;
  /** ISO country codes, or `["*"]` for everywhere. All entries ship as `*` today. */
  countries: string[];
  /**
   * Units on which the driver may answer N/A. Scoped per unit, not per
   * question: indicators are optional on a cargo bike, mandatory on an HGV.
   */
  naFor?: string[];
}

export interface CheckQuestionCatalogue {
  version: number;
  sections: CheckQuestionCatalogueSection[];
  questions: CheckQuestionCatalogueEntry[];
}

// ────────────────────────────────────────────────────────────────────────────
// Templates
// ────────────────────────────────────────────────────────────────────────────

/** Row shape for the templates grid. */
export interface CheckTemplateSummary {
  id: string;
  name: string;
  regime: CheckRegime;
  unitType: UnitType;
  wacType: WacType | null;
  /** Published once a published version exists, otherwise Draft. */
  status: TemplateVersionStatus;
  version: number;
  questionCount: number;
  /** True when an editable draft sits alongside a published version. */
  hasUnpublishedDraft: boolean;
  updatedUtc: string;
  updatedBy: string;
}

export interface CheckTemplateQuestion {
  id: string;
  /** Catalogue provenance, or null for an operator-added custom question. */
  catalogueKey: string | null;
  sectionKey: string;
  sectionLabel: string;
  label: string;
  sortOrder: number;
  isMandatory: boolean;
  defaultSeverity: QuestionSeverity;
  allowNotApplicable: boolean;
  requirePhotoOnDefect: boolean;
}

/** One template plus the questions of its effective version (draft if any). */
export interface CheckTemplate extends Omit<CheckTemplateSummary, 'questionCount'> {
  versionId: string;
  questions: CheckTemplateQuestion[];
}

export interface CreateCheckTemplateRequest {
  organisationId: string;
  name: string;
  regime: CheckRegime;
  unitType: UnitType;
  /** Required when regime is Wac, must be null for PreUse. */
  wacType: WacType | null;
}

/** One question as sent to the draft-update endpoint. Ids are not round-tripped. */
export interface DraftQuestionInput {
  catalogueKey: string | null;
  sectionKey: string;
  sectionLabel: string;
  label: string;
  sortOrder: number;
  isMandatory: boolean;
  defaultSeverity: QuestionSeverity;
  allowNotApplicable: boolean;
  requirePhotoOnDefect: boolean;
}

/**
 * PUT `/check/api/v1/checktemplates/{id}` body. Replaces the draft's name and
 * whole question list — whole-list rather than per-question patches, so a
 * reorder is a single write. If the template is published with no draft, the
 * BE forks one from the published version first; published versions are never
 * edited in place.
 */
export interface UpdateCheckTemplateDraftRequest {
  id: string;
  organisationId: string;
  name: string;
  questions: DraftQuestionInput[];
}

export interface DuplicateCheckTemplateRequest {
  id: string;
  organisationId: string;
  name: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Recorded checks
// ────────────────────────────────────────────────────────────────────────────

/** `CheckStatus`. A check sits InProgress until the driver submits it. */
export type CheckStatus = 'InProgress' | 'Submitted';

/** `CheckOutcome` — the roll-up across a check's answers. */
export type CheckOutcome = 'Pass' | 'Defect' | 'Advisory';

/** `AnswerValue` — one answer. NotApplicable only where the template allows it. */
export type AnswerValue = 'Ok' | 'Defect' | 'NotApplicable';

export type DefectStatus = 'Open' | 'InProgress' | 'Deferred' | 'Resolved';

/**
 * A resource's most recent submitted check — the Last Check / Status columns
 * on the Vehicles and Drivers grids.
 *
 * One row per resource that HAS been checked. A resource with no check is
 * absent from the list rather than present with nulls, which is what lets the
 * grid treat "no entry" as Not done.
 */
export interface ResourceCheckSummary {
  resourceId: string;
  lastCheckedUtc: string;
  outcome: CheckOutcome;
}

/**
 * One row in the checks list — a single inspected resource, not the whole
 * walk-around. A driver checking a unit and its trailer produces two of these.
 */
export interface CheckSummary {
  id: string;
  checkSessionId: string;
  resourceId: string;
  resourceRegistration: string;
  templateName: string;
  templateVersion: number;
  unitType: UnitType;
  regime: CheckRegime;
  status: CheckStatus;
  outcome: CheckOutcome;
  startedUtc: string;
  completedUtc: string | null;
  performedByName: string;
  odometerKm: number | null;
  answerCount: number;
  failedCount: number;
  notApplicableCount: number;
  defectCount: number;
}

export interface CheckAnswer {
  id: string;
  sectionKey: string;
  sectionLabel: string;
  label: string;
  sortOrder: number;
  answer: AnswerValue;
  note: string | null;
  /** The blob is fetched separately via a short-lived URL; this is just presence. */
  hasPhoto: boolean;
  photoFileName: string | null;
}

export interface CheckDefect {
  id: string;
  title: string;
  severity: QuestionSeverity;
  status: DefectStatus;
}

/** Another resource inspected in the same walk-around — typically the trailer. */
export interface CheckSibling {
  id: string;
  resourceRegistration: string;
  outcome: CheckOutcome;
}

export interface CheckDetail {
  summary: CheckSummary;
  declarationAccepted: boolean;
  declarationUtc: string | null;
  hasSignature: boolean;
  sessionSiblings: CheckSibling[];
  answers: CheckAnswer[];
  defects: CheckDefect[];
}

/** Server-side filters. Everything optional. */
export interface CheckListFilter {
  resourceId?: string;
  outcome?: CheckOutcome;
  status?: CheckStatus;
  fromUtc?: string;
  toUtc?: string;
  search?: string;
  take?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Defects
// ────────────────────────────────────────────────────────────────────────────

export interface DefectSummary {
  id: string;
  resourceId: string;
  resourceRegistration: string;
  title: string;
  description: string | null;
  severity: QuestionSeverity;
  status: DefectStatus;
  garageName: string | null;
  dueUtc: string | null;
  /** Past its due date and still unresolved. Computed server-side. */
  isOverdue: boolean;
  reportedByName: string;
  reportedUtc: string;
  resolvedUtc: string | null;
  /** The check that raised it, when it came from one rather than being manual. */
  checkId: string | null;
  /** The answer on that check that raised it — what its photo hangs off. */
  checkAnswerId: string | null;
  /** Whether the driver photographed it. */
  hasPhoto: boolean;
  actionCount: number;
}

export interface DefectAction {
  id: string;
  note: string;
  statusAfter: DefectStatus;
  garageName: string | null;
  recordedBy: string;
  recordedUtc: string;
}

export interface DefectDetail {
  summary: DefectSummary;
  /** The question whose failure raised this. Null for a manual defect. */
  sourceQuestionLabel: string | null;
  /** What the driver typed at the time. */
  sourceNote: string | null;
  actions: DefectAction[];
}

export interface DefectListFilter {
  status?: DefectStatus;
  severity?: QuestionSeverity;
  resourceId?: string;
  overdueOnly?: boolean;
  unresolvedOnly?: boolean;
  search?: string;
  take?: number;
}

/**
 * POST body for appending an action. The only way a defect's status, garage or
 * due date changes — there is no edit-defect endpoint, so every change carries
 * a note and an author.
 */
export interface RecordDefectActionRequest {
  defectId: string;
  organisationId: string;
  note: string;
  statusAfter: DefectStatus;
  garageName?: string | null;
  /** Null leaves any existing deadline untouched. */
  dueUtc?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Template assignment
// ────────────────────────────────────────────────────────────────────────────

/**
 * Which template a resource is checked with. Lives in My.Check rather than on
 * Directory's Resource, because Directory's ResourceType has no room for Lgv,
 * Bus, Minibus, Car, Motorcycle or Cycle — naming the template directly avoids
 * asking Directory to grow a taxonomy TMS and Depot would also inherit.
 */
export interface ResourceCheckAssignment {
  resourceId: string;
  checkTemplateId: string;
  templateName: string;
  unitType: UnitType;
  regime: CheckRegime;
  wacType: WacType | null;
  /** False when the assigned template has never been published. */
  isPublished: boolean;
}

/** PUT body. A null template clears the assignment for every listed resource. */
export interface SetResourceCheckAssignmentsRequest {
  organisationId: string;
  resourceIds: string[];
  checkTemplateId: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint map
// ────────────────────────────────────────────────────────────────────────────

const BASE = '/check/api/v1';

export const checkApi = {
  /** GET the Check API's `info/api-info` — confirms the gateway route works. */
  getApiInfo: () => request.get<ApiInfo>(`${BASE}/info/api-info`),

  // ── Question catalogue ──────────────────────────────────────────────────

  /**
   * The default question catalogue. Pass `unit` to narrow to one unit type —
   * the response keeps the same envelope either way, with sections trimmed to
   * those that still have questions.
   *
   * Tenant-agnostic and identical for every caller, so cache it hard.
   */
  getQuestionCatalogue: (unit?: UnitType, country?: string) => {
    const qs = new URLSearchParams();
    if (unit) qs.set('unit', unit);
    if (country) qs.set('country', country);
    const suffix = qs.size > 0 ? `?${qs}` : '';
    return request.get<CheckQuestionCatalogue>(`${BASE}/checkquestioncatalogue${suffix}`);
  },

  // ── Templates ───────────────────────────────────────────────────────────

  getTemplates: (organisationId: string) =>
    request.get<CheckTemplateSummary[]>(
      `${BASE}/checktemplates?organisationId=${organisationId}`,
    ),

  getTemplate: (id: string, organisationId: string) =>
    request.get<CheckTemplate>(
      `${BASE}/checktemplates/${id}?organisationId=${organisationId}`,
    ),

  /** Returns the new template's id. Forks the catalogue into draft v1. */
  createTemplate: (body: CreateCheckTemplateRequest) =>
    request.post<string>(`${BASE}/checktemplates`, body),

  /** Returns the template id. */
  updateTemplateDraft: (body: UpdateCheckTemplateDraftRequest) =>
    request.put<string>(`${BASE}/checktemplates/${body.id}`, body),

  /**
   * Freeze the draft and make it live. Returns the published version's id.
   * Rejects a Wac template that omits any mandatory catalogue question — the
   * error message names the questions to restore.
   */
  publishTemplate: (id: string, organisationId: string) =>
    request.put<string>(
      `${BASE}/checktemplates/${id}/publish?organisationId=${organisationId}`,
      {},
    ),

  /** Returns the copy's id. The copy always starts as an unpublished draft. */
  duplicateTemplate: (body: DuplicateCheckTemplateRequest) =>
    request.post<string>(`${BASE}/checktemplates/${body.id}/duplicate`, body),

  /**
   * Soft-delete a template. Refused with a 409 once any check has been
   * recorded against it — a completed check resolves its question text through
   * its template version.
   */
  deleteTemplate: (id: string, organisationId: string) =>
    request.del<string>(`${BASE}/checktemplates/${id}?organisationId=${organisationId}`),

  // ── Recorded checks (read-only — drivers create them in myDriver) ───────

  getChecks: (organisationId: string, filter: CheckListFilter = {}) => {
    const qs = new URLSearchParams({ organisationId });
    if (filter.resourceId) qs.set('resourceId', filter.resourceId);
    if (filter.outcome) qs.set('outcome', filter.outcome);
    if (filter.status) qs.set('status', filter.status);
    if (filter.fromUtc) qs.set('fromUtc', filter.fromUtc);
    if (filter.toUtc) qs.set('toUtc', filter.toUtc);
    if (filter.search) qs.set('search', filter.search);
    if (filter.take) qs.set('take', String(filter.take));
    return request.get<CheckSummary[]>(`${BASE}/checks?${qs}`);
  },

  getCheck: (id: string, organisationId: string) =>
    request.get<CheckDetail>(`${BASE}/checks/${id}?organisationId=${organisationId}`),

  /**
   * A defect photo, as bytes. Fetched rather than linked: an <img src> cannot
   * carry the bearer token, so the image comes through the same authenticated
   * pipeline as everything else and is shown from an object URL.
   */
  getCheckAnswerPhoto: (checkId: string, answerId: string) =>
    request.get<Blob>(`${BASE}/checks/${checkId}/answers/${answerId}/photo`, { responseType: 'blob' }),

  /** The driver's signature on a check (SVG), as bytes. */
  getCheckSignature: (checkId: string) =>
    request.get<Blob>(`${BASE}/checks/${checkId}/signature`, { responseType: 'blob' }),

  /**
   * Latest check per resource, fetched whole. There is at most one row per
   * resource, so the grid joins client-side rather than asking per visible row.
   */
  getResourceCheckSummaries: (organisationId: string) =>
    request.get<ResourceCheckSummary[]>(
      `${BASE}/checks/resource-summaries?organisationId=${organisationId}`,
    ),

  // ── Defects (no create — a defect comes from a failed check answer) ─────

  getDefects: (organisationId: string, filter: DefectListFilter = {}) => {
    const qs = new URLSearchParams({ organisationId });
    if (filter.status) qs.set('status', filter.status);
    if (filter.severity) qs.set('severity', filter.severity);
    if (filter.resourceId) qs.set('resourceId', filter.resourceId);
    if (filter.overdueOnly) qs.set('overdueOnly', 'true');
    if (filter.unresolvedOnly) qs.set('unresolvedOnly', 'true');
    if (filter.search) qs.set('search', filter.search);
    if (filter.take) qs.set('take', String(filter.take));
    return request.get<DefectSummary[]>(`${BASE}/defects?${qs}`);
  },

  getDefect: (id: string, organisationId: string) =>
    request.get<DefectDetail>(`${BASE}/defects/${id}?organisationId=${organisationId}`),

  /** Returns the new action's id. */
  recordDefectAction: (body: RecordDefectActionRequest) =>
    request.post<string>(`${BASE}/defects/${body.defectId}/actions`, body),

  // ── Template assignment ────────────────────────────────────────────────

  getResourceCheckAssignments: (organisationId: string) =>
    request.get<ResourceCheckAssignment[]>(
      `${BASE}/resourcecheckassignments?organisationId=${organisationId}`,
    ),

  /** Returns how many resources were affected. */
  setResourceCheckAssignments: (body: SetResourceCheckAssignmentsRequest) =>
    request.put<number>(`${BASE}/resourcecheckassignments`, body),
};

export type { ApiInfo };
